import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Box, Button, Typography, Slider } from '@mui/material';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';

import type { InteractiveWidgetProps } from './widgetRegistry';

// ---------------------------------------------------------------------------
// TimerWidget
//
// A countdown timer with preset durations. State is transient (React only).
// When the timer reaches 00:00 it plays a short chime via the Web Audio API
// and visually indicates completion.
// ---------------------------------------------------------------------------

const PRESETS = [
  { label: '1m', seconds: 60 },
  { label: '2m', seconds: 120 },
  { label: '3m', seconds: 180 },
  { label: '5m', seconds: 300 },
  { label: '10m', seconds: 600 },
  { label: '20m', seconds: 1200 },
  { label: '30m', seconds: 1800 },
];

/** Format seconds into MM:SS. */
function formatTime(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/**
 * Play a short completion chime using the Web Audio API.
 * Two descending tones (~880 Hz → 660 Hz) for a pleasant "ding-dong".
 */
function playChime(): void {
  try {
    const ctx = new AudioContext();
    const now = ctx.currentTime;

    // First tone
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = 'sine';
    osc1.frequency.value = 880;
    gain1.gain.setValueAtTime(0.3, now);
    gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
    osc1.connect(gain1).connect(ctx.destination);
    osc1.start(now);
    osc1.stop(now + 0.5);

    // Second tone (slight delay)
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'sine';
    osc2.frequency.value = 660;
    gain2.gain.setValueAtTime(0.3, now + 0.15);
    gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.7);
    osc2.connect(gain2).connect(ctx.destination);
    osc2.start(now + 0.15);
    osc2.stop(now + 0.7);

    // Clean up AudioContext after tones finish
    setTimeout(() => ctx.close(), 1000);
  } catch {
    // AudioContext may not be available in all environments — fail silently
  }
}

const TimerWidget: React.FC<InteractiveWidgetProps> = ({
  config,
  interactive,
  width,
  height,
  onConfigChange,
}) => {
  const initialDuration = typeof config.durationSeconds === 'number' ? config.durationSeconds : 300;
  const [duration, setDuration] = useState(initialDuration);
  const [remainingMs, setRemainingMs] = useState(initialDuration * 1000);
  const [running, setRunning] = useState(false);
  const [finished, setFinished] = useState(false);
  const animationFrameRef = useRef<number | null>(null);
  const endTimeRef = useRef<number | null>(null);
  const remainingMsRef = useRef(initialDuration * 1000);

  const clearAnimationFrame = useCallback(() => {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, []);

  const setRemainingMsValue = useCallback((value: number) => {
    remainingMsRef.current = value;
    setRemainingMs(value);
  }, []);

  // Sync if config changes externally
  useEffect(() => {
    const d = typeof config.durationSeconds === 'number' ? config.durationSeconds : 300;
    setDuration(d);
    setRemainingMsValue(d * 1000);
    setRunning(false);
    setFinished(false);
  }, [config.durationSeconds, setRemainingMsValue]);

  // Countdown animation
  useEffect(() => {
    clearAnimationFrame();
    if (!running || remainingMsRef.current <= 0) return;

    endTimeRef.current = performance.now() + remainingMsRef.current;

    const tick = () => {
      if (endTimeRef.current === null) return;

      const nextRemainingMs = Math.max(0, endTimeRef.current - performance.now());
      setRemainingMsValue(nextRemainingMs);

      if (nextRemainingMs <= 0) {
        setRunning(false);
        setFinished(true);
        endTimeRef.current = null;
        playChime();
        return;
      }

      animationFrameRef.current = requestAnimationFrame(tick);
    };

    animationFrameRef.current = requestAnimationFrame(tick);
    return clearAnimationFrame;
  }, [clearAnimationFrame, running, setRemainingMsValue]);

  const handleStartPause = useCallback(() => {
    if (!interactive) return;
    if (finished) return;
    setRunning((prev) => !prev);
  }, [interactive, finished]);

  const handleReset = useCallback(() => {
    if (!interactive) return;
    setRunning(false);
    endTimeRef.current = null;
    setRemainingMsValue(duration * 1000);
    setFinished(false);
  }, [duration, interactive, setRemainingMsValue]);

  const handlePreset = useCallback(
    (seconds: number) => {
      setDuration(seconds);
      endTimeRef.current = null;
      setRemainingMsValue(seconds * 1000);
      setRunning(false);
      setFinished(false);
      onConfigChange?.({ durationSeconds: seconds });
    },
    [onConfigChange, setRemainingMsValue]
  );

  const handleProgressChange = useCallback(
    (event: Event, value: number | number[]) => {
      event.stopPropagation();
      if (!interactive || duration <= 0) return;

      const progressValue = Array.isArray(value) ? value[0] : value;
      const clampedProgress = Math.max(0, Math.min(100, progressValue));
      const nextRemainingMs = Math.max(
        0,
        Math.min(duration * 1000, duration * 1000 * (1 - clampedProgress / 100))
      );

      setRemainingMsValue(nextRemainingMs);
      setFinished(nextRemainingMs === 0);
      if (running) endTimeRef.current = performance.now() + nextRemainingMs;
      if (nextRemainingMs === 0) {
        setRunning(false);
        endTimeRef.current = null;
      }
    },
    [duration, interactive, running, setRemainingMsValue]
  );

  // Responsive sizing
  const baseFontSize = Math.max(10, Math.min(width, height) * 0.07);
  const timeFontSize = Math.max(18, Math.min(width * 0.18, height * 0.28));
  const remaining = Math.ceil(remainingMs / 1000);
  const progress = duration > 0 ? ((duration * 1000 - remainingMs) / (duration * 1000)) * 100 : 0;
  const progressColor = finished
    ? '#4caf50'
    : remainingMs <= 10000 && remainingMs > 0
      ? '#ff5252'
      : '#f09a36';
  const progressHeight = Math.max(4, height * 0.015);
  const progressThumbSize = Math.max(10, Math.min(16, height * 0.04));

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#1e1e1e',
        borderRadius: 1,
        overflow: 'hidden',
        userSelect: 'none',
        pointerEvents: interactive ? 'auto' : 'none',
      }}
      onMouseDown={interactive ? (e) => e.stopPropagation() : undefined}
    >
      {/* Time display — stop propagation on preset clicks in edit mode too */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 1,
        }}
      >
        <Typography
          sx={{
            color: finished
              ? '#4caf50'
              : remaining <= 10 && remaining > 0 && running
                ? '#ff5252'
                : '#fff',
            fontSize: timeFontSize,
            fontFamily: 'monospace',
            fontWeight: 700,
            lineHeight: 1,
            transition: 'color 0.3s',
          }}
        >
          {formatTime(remaining)}
        </Typography>
      </Box>

      {/* Progress bar */}
      <Slider
        aria-label="Timer progress"
        value={progress}
        min={0}
        max={100}
        step={0.01}
        onChange={handleProgressChange}
        onMouseDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        slotProps={{
          input: {
            tabIndex: interactive ? 0 : -1,
          },
        }}
        sx={{
          alignSelf: 'stretch',
          mx: 1,
          py: Math.max(4, progressThumbSize / 2),
          width: 'auto',
          height: progressHeight,
          color: progressColor,
          cursor: interactive ? 'pointer' : 'default',
          pointerEvents: interactive ? 'auto' : 'none',
          '& .MuiSlider-rail': {
            bgcolor: '#333',
            borderRadius: 1,
            height: progressHeight,
            opacity: 1,
          },
          '& .MuiSlider-track': {
            bgcolor: progressColor,
            border: 'none',
            borderRadius: 1,
            height: progressHeight,
            transition: 'background-color 0.3s',
          },
          '& .MuiSlider-thumb': {
            width: progressThumbSize,
            height: progressThumbSize,
            bgcolor: progressColor,
            border: '2px solid #1e1e1e',
            boxShadow: 'none',
            transition: 'background-color 0.3s, box-shadow 0.2s',
            '&:hover, &.Mui-focusVisible': {
              boxShadow: interactive ? '0 0 0 6px rgba(240, 154, 54, 0.18)' : 'none',
            },
            '&.Mui-active': {
              boxShadow: interactive ? '0 0 0 8px rgba(240, 154, 54, 0.24)' : 'none',
            },
          },
        }}
      />

      {/* Preset buttons — always interactive so users can configure duration in edit mode */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: '2px',
          px: 0.5,
          py: 0.5,
          flexWrap: 'wrap',
          pointerEvents: 'auto',
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {PRESETS.map((p) => (
          <Button
            key={p.seconds}
            onClick={() => handlePreset(p.seconds)}
            size="small"
            sx={{
              minWidth: 0,
              px: 0.75,
              py: 0.25,
              fontSize: baseFontSize * 0.75,
              fontWeight: duration === p.seconds ? 700 : 400,
              color: duration === p.seconds ? '#fff' : '#aaa',
              bgcolor: duration === p.seconds ? '#f09a36' : '#333',
              borderRadius: 0.5,
              lineHeight: 1.2,
              '&:hover': {
                bgcolor: duration === p.seconds ? '#d4882e' : '#4a4a4a',
              },
            }}
          >
            {p.label}
          </Button>
        ))}
      </Box>

      {/* Controls */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 1,
          px: 1,
          pb: 1,
        }}
      >
        <Button
          onClick={handleStartPause}
          tabIndex={interactive ? 0 : -1}
          disabled={finished && remaining === 0}
          sx={{
            minWidth: 0,
            flex: 1,
            maxWidth: 120,
            py: 0.5,
            fontSize: baseFontSize,
            fontWeight: 600,
            color: '#fff',
            bgcolor: running ? '#e57373' : '#4caf50',
            borderRadius: 1,
            '&:hover': {
              bgcolor: running ? '#c62828' : '#388e3c',
            },
            '&.Mui-disabled': {
              color: '#666',
              bgcolor: '#333',
            },
          }}
        >
          {running ? (
            <PauseRoundedIcon fontSize="small" />
          ) : (
            <PlayArrowRoundedIcon fontSize="small" />
          )}
        </Button>
        <Button
          onClick={handleReset}
          tabIndex={interactive ? 0 : -1}
          sx={{
            minWidth: 0,
            px: 1.5,
            py: 0.5,
            fontSize: baseFontSize,
            color: '#aaa',
            bgcolor: '#333',
            borderRadius: 1,
            '&:hover': {
              bgcolor: '#4a4a4a',
            },
          }}
        >
          <ReplayRoundedIcon fontSize="small" />
        </Button>
      </Box>
    </Box>
  );
};

export default React.memo(TimerWidget);
